import {
  Stack,
  Link,
  HStack,
  Text,
  Button,
  Alert,
  Box,
} from "@chakra-ui/react";
import { Link as RouterLink, useActionData } from "@remix-run/react";
import type { LoaderArgs, MetaFunction } from "@remix-run/node";
import { T, t } from "~/libs/i18n";
import { withZod } from "@remix-validated-form/with-zod";
import { z } from "zod";
import { ValidatedForm } from "remix-validated-form";
import {
  ValidatedCheckbox,
  ValidatedCheckboxGroup,
  ValidatedInput,
} from "~/components/forms/validated-inputs.tsx";
import { SubmitButton } from "~/components/forms/form-buttons.tsx";
import { PasswordPolicyChecks } from "~/components/password-policy-checks.tsx";
import { useState } from "react";
import { formValidationError, serverError } from "~/utils/response-handlers.ts";
import { IdentityApi } from "~/api/identity-api.ts";
import { AuthSession } from "~/utils/session.server.ts";
import { $path } from "remix-routes";
import { json } from "@remix-run/node";

export const meta: MetaFunction = () => {
  return [{ title: `${t("Create an account")} - Preventure` }];
};

const signUpFormValidator = withZod(
  z
    .object({
      email: z.string().email().min(1, t("Enter your email address")),
      firstName: z.string().min(1, t("First name is required")),
      lastName: z.string().min(1, t("Last name is required")),
      password: z.string().min(1, t("Enter a valid password")),
      passwordConfirmation: z.string().optional(),
      policyAgreement: z.enum(["policyAgreement"]).optional(),
    })
    .superRefine((data, ctx) => {
      if (data.password !== data.passwordConfirmation) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("Passwords don't match"),
          path: ["passwordConfirmation"],
        });
      }
      if (data.policyAgreement !== "policyAgreement") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("Please indicate that you read and agree to the policies"),
          path: ["policyAgreement"],
        });
      }
    }),
);

export async function loader({ request, params, context }: LoaderArgs) {
  return null;
}

export async function action({ request, params, context }: LoaderArgs) {
  const body = await request.formData();
  const submission = await signUpFormValidator.validate(body);
  if (submission.error) {
    return formValidationError({ fieldErrors: submission.error.fieldErrors });
  }

  const { email, firstName, lastName, password } = submission.data;
  const response = await IdentityApi.SignupWithEmail(request, {
    email,
    firstName,
    lastName,
    password,
  });
  if (!response.ok) {
    return await serverError({ error: response.error });
  }

  const session = await new AuthSession(request);
  return await session.login({
    response: json(response, response.init),
    redirectTo: $path("/join"),
  });
}

export default function AuthSignUp() {
  const [passwordInput, setPasswordInput] = useState("");
  const actionData = useActionData<typeof action>();
  let errorMessage: string | undefined;

  if (actionData?.type === "SERVER_ERROR") {
    errorMessage = actionData.error.description;
  }

  return (
    <>
      <Stack mb={8}>
        <Text textStyle={"heading"}>
          <T>Create an account</T>
        </Text>
        {errorMessage && (
          <Box maxW={"md"}>
            <Alert status={"error"}>
              <Text>{errorMessage}</Text>
            </Alert>
          </Box>
        )}
        <ValidatedForm validator={signUpFormValidator} method={"post"}>
          <Stack w={"full"}>
            <ValidatedInput name={"email"} type={"email"} label={t("Email")} />
            <ValidatedInput
              name={"firstName"}
              type={"text"}
              label={t("First name")}
            />
            <ValidatedInput
              name={"lastName"}
              type={"text"}
              label={t("Last name")}
            />
            <ValidatedInput
              name={"password"}
              type={"password"}
              label={t("Password")}
              onChange={e => setPasswordInput(e.target.value)}
            />

            <PasswordPolicyChecks password={passwordInput} />

            <ValidatedInput
              name={"passwordConfirmation"}
              type={"password"}
              label={t("Confirm password")}
            />

            <Stack>
              <HStack justifyContent={"center"} spacing={8}>
                <Link href={$path("/public/terms-of-use")} target={"_blank"}>
                  <T>Terms & Conditions</T>
                </Link>
                <Link href={$path("/public/privacy-policy")} target={"_blank"}>
                  <T>Privacy Policy</T>
                </Link>
              </HStack>
              <ValidatedCheckboxGroup name={"policyAgreement"}>
                <ValidatedCheckbox
                  name={"policyAgreement"}
                  value={"policyAgreement"}
                  label={t(
                    "I have read and agree to the Terms & Conditions and Privacy Policy",
                  )}
                />
              </ValidatedCheckboxGroup>
            </Stack>

            <Stack
              w={"full"}
              alignItems={"center"}
              spacing={4}
              px={{ sm: 2, md: 12 }}
              py={4}
            >
              <SubmitButton w={"full"}>
                <T>Continue</T>
              </SubmitButton>
              <Button
                as={RouterLink}
                variant={"ghost"}
                w={"full"}
                to={"/login"}
              >
                <T>Cancel</T>
              </Button>
            </Stack>
          </Stack>
        </ValidatedForm>
      </Stack>
    </>
  );
}
